import { HttpErrorResponse } from '@angular/common/http';

const DEFAULT_CONNECTION_LOST_MESSAGE =
  'Looks like you lost your connection. Please check your connection and try again.';

/**
 * Backend ticket-creation errors (`AppError`) can be wrapped in multiple nested
 * `errorList`/`message` layers, and the innermost payload can itself be either a
 * plain string or an express-validator-style `{ msg, param }[]` array. The array
 * form can appear at any nesting depth, so it must be checked on every iteration
 * of the unwrap loop, not just once before the loop starts.
 */
export function extractTicketErrorMessage(
  error: HttpErrorResponse,
  fallback: string,
): string {
  let current: unknown =
    error?.error?.error?.errorList ?? error?.error?.error?.message;
  const visited = new Set<unknown>();

  while (current && typeof current === 'object' && !visited.has(current)) {
    visited.add(current);

    if (Array.isArray(current)) {
      const first = current[0] as Record<string, unknown> | undefined;
      current = first?.['msg'] ?? first?.['message'];
      continue;
    }

    const wrapper = current as Record<string, unknown>;
    current = wrapper['errorList'] ?? wrapper['message'];
  }

  if (typeof current === 'string' && current.trim()) {
    return current;
  }

  const topLevelMessage = error?.error?.error?.message;
  return typeof topLevelMessage === 'string' && topLevelMessage.trim()
    ? topLevelMessage
    : fallback;
}

/**
 * Resolves the subtitle text for a ticket-creation "could not be
 * submitted/generated" failure dialog: a genuine connection failure (no server
 * response) gets a dedicated message, otherwise the real backend error is
 * extracted via {@link extractTicketErrorMessage}.
 */
export function resolveTicketErrorMessage(
  error: HttpErrorResponse,
  fallback: string,
  connectionLostMessage: string = DEFAULT_CONNECTION_LOST_MESSAGE,
): string {
  const hasServerResponse = error.status !== 0 && !!error.error;
  if (!hasServerResponse) {
    return connectionLostMessage;
  }
  return extractTicketErrorMessage(error, fallback);
}
